'use client';

import NextLink from 'next/link';
import Image from 'next/image';
import { useState, useEffect, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import debounce from 'lodash.debounce';

import headerLogo from 'assets/logo-svg.svg';
import searchIcon from 'assets/search.png';
import closeSearch from 'assets/close-filled.png';
import { flatListToHierarchical, formatCategoryUrl } from 'services/lib/util';
import RecursiveMenuItem from './recursive-menu-item';
import MobileRecursiveMenuItem from './recursive-menu-item-mobile';

const HeaderContent = ({ headerMenus = [] }) => {
  const { register, handleSubmit, watch, setValue } = useForm();
  const [isActive, setIsActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const rawMenuItems = headerMenus?.[0]?.menuItems?.nodes || [];
  const hierarchicalMenu = flatListToHierarchical(rawMenuItems);

  // INITIATE & SYNC FORM WITH URL
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    setValue('search', searchFromUrl);
    if (searchFromUrl) {
      setIsActive(true);
    }
  }, [searchParams, setValue]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style = null;
    }
  }, [isOpen]);

  const handleClick = () => setIsActive((current) => !current);
  const handleMenuClick = () => setIsOpen((current) => !current);

  const handleSearchClose = () => {
    setIsActive(false);
    setValue('search', '');

    if (pathname === '/szukaj') {
      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.delete('search');
      currentParams.delete('page');
      router.push(`/szukaj?${currentParams.toString()}`, { scroll: false });
    }
  };

  const handleMobileMenuClick = (path) => () => {
    const newPath = formatCategoryUrl(path);
    router.push(newPath);
    setIsActive(false);
    setIsOpen(false);
  };

  const formSubmitHandler = (formData) => {
    if (pathname === '/szukaj') {
      const currentParams = new URLSearchParams(searchParams.toString());

      if (formData.search) {
        currentParams.set('search', formData.search);
      } else {
        currentParams.delete('search');
      }

      currentParams.delete('page');
      router.push(`/szukaj?${currentParams.toString()}`, { scroll: false });
    } else {
      if (formData.search) {
        router.push(`/szukaj?search=${formData.search}`);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFormSubmitHandler = debounce(formSubmitHandler, 500);

  useEffect(() => {
    const subscription = watch(handleSubmit(debouncedFormSubmitHandler));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, debouncedFormSubmitHandler]);

  return (
    <div className="w-full max-w-full basis-full px-0">
      <header className="headerWrapper px-0">
        <div className="desktopBar px-0">
          {pathname === '/' ? (
            <h1 className="logo px-0">
              <NextLink href="/" passHref className="logoLink px-0">
                {headerLogo && <Image src={headerLogo} alt="Canal+" width={182} height={28} className="px-0" />}
              </NextLink>
            </h1>
          ) : (
            <div className="logo px-0">
              <NextLink href="/" passHref className="logoLink px-0">
                {headerLogo && <Image src={headerLogo} alt="Canal+" width={182} height={28} className="px-0" />}
              </NextLink>
            </div>
          )}

          <div className={`headerMenu px-0 ${isActive ? 'hide' : ''}`}>
            <nav className="px-0">
              {hierarchicalMenu?.length > 0 && (
                <ul className="m-0 p-0 px-0">
                  {hierarchicalMenu.map((menuItem) => (
                    <RecursiveMenuItem key={menuItem.id} item={menuItem} />
                  ))}
                </ul>
              )}
            </nav>
          </div>

          <div className={`mobileMenu px-0 ${isOpen ? 'open' : ''}`} onClick={handleMenuClick}>
            <span className="px-0"></span>
            <span className="px-0"></span>
            <span className="px-0"></span>
          </div>

          <div className={`searchWrapper px-0 ${isActive ? 'active' : ''}`}>
            <button
              type="button"
              className="openSearch flex cursor-pointer items-center justify-center border-none bg-transparent p-0 px-0"
              onClick={handleClick}
            >
              <Image src={searchIcon} alt="Search" width={20} height={20} className="px-0" />
            </button>
            <form className="searchForm px-0" onSubmit={handleSubmit(formSubmitHandler)}>
              <input
                type="text"
                className={`searchInput px-0 ${isActive ? 'active' : ''}`}
                placeholder="Szukaj..."
                name="search"
                {...register('search')}
                aria-label="Szukaj"
              />
            </form>
            <button
              type="button"
              className={`closeSearch flex cursor-pointer items-center justify-center border-none bg-transparent p-0 px-0 ${isActive ? 'active' : ''}`}
              onClick={handleSearchClose}
            >
              <Image src={closeSearch} alt="close" width={20} height={20} className="px-0" />
            </button>
          </div>
        </div>
        <div className={`mobileBar px-0 ${isActive ? 'hide' : ''} ${isOpen ? 'open' : ''}`}>
          <div className="headerMenu px-0">
            <nav className="px-0">
              {hierarchicalMenu?.length > 0 && (
                <ul className="m-0 p-0 px-0">
                  {hierarchicalMenu.map((menuItem) => (
                    <MobileRecursiveMenuItem
                      key={menuItem.id}
                      item={menuItem}
                      handleMenuClick={handleMobileMenuClick}
                    />
                  ))}
                </ul>
              )}
            </nav>
          </div>
        </div>
      </header>
    </div>
  );
};

const Header = ({ headerMenus }) => {
  return (
    <Suspense
      fallback={
        <div className="w-full max-w-full basis-full px-0">
          <header className="headerWrapper px-0"></header>
        </div>
      }
    >
      <HeaderContent headerMenus={headerMenus} />
    </Suspense>
  );
};

export default Header;
